/**
 * This file defines the `AscTimeSpentProvider` component and `AscTimeSpentContext` context, which are used to track and log
 * the time spent by users on the application. The component sets up intervals to periodically save and send the time spent
 * to the API, and uses local storage to persist the time spent data across sessions. The component also uses sessions
 * to prevent other open tabs from logging time while one tab is already logging.
 */

import dayjs from 'dayjs';
import React, { useEffect, useMemo, useState } from 'react';
import { STORAGE_KEYS } from '../../constants';
import { useAuthContext, useWindowFocus } from '../../hooks';
import { batchLogTimeSpentApi } from '../../services/time-spent/time-spent.api';
import { v4 as uuidv4 } from 'uuid';

type EndTime = string;
type StartTime = string;

interface TimeSpentMap {
  [startTime: StartTime]: EndTime;
}

const ONE_MINUTE = 60 * 1000;
const TEN_MINUTES_AND_10_SECONDS = 10.1 * ONE_MINUTE;

interface AscTimeSpentProviderProps {
  children: React.ReactNode;
}

/**
 * The concept of a session is used to prevent other open tabs from logging time while one tab is already logging.
 * A session is defined as a unique ID and a timestamp. The timestamp is updated every time the session is refreshed.
 * The session is refreshed periodically as long as the tab is focused, and the session is reset every time AscTimeSpentProvider mounts.
 * The session is refreshed every time the save time spent interval runs, and the save time spent interval runs every minute.
 *
 * The session ID is used to identify the session. The session ID is unique per session.
 * The session string is the session ID and a timestamp separated by an underscore. The session string is unique per session.
 * The sessionKey is used to store the session string in local storage. The sessionKey is unique per user.
 * The timeSpentKey is used to store the time spent in local storage. The timeSpentKey is unique per user.
 *
 * Time spent is stored as a map of start times to end times. The start times are the keys and the end times are the values.
 * The start times are the start of the minute when the user started the activity. The end times are the start of the minute when the user ended the activity.
 * The time spent is saved to local storage every minute, and the time spent is sent to the API every 10 minutes and 10 seconds.
 * The time spent is sent to the API as an array of time spent intervals.
 *
 * When the window loses focus, the save time spent intervals are cleared, startTime is reset and the session is not refreshed.
 * Meaning that when the next time time spent is saved, it will be saved as a new session.
 */
export const AscTimeSpentContext = React.createContext({});

export function AscTimeSpentProvider({ children }: AscTimeSpentProviderProps) {
  // Create refs for the save and send intervals
  const saveTimeSpentIntervalRef = React.useRef<NodeJS.Timer>();
  const sendSavedTimeSpentIntervalRef = React.useRef<NodeJS.Timer>();
  // Set the start time to the current time
  const [startTime, setStartTime] = useState(dayjs());
  const { user, isAuthenticated } = useAuthContext();
  const isWindowFocused = useWindowFocus();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);
  // Generate the time spent key, session key, and session ID
  const { timeSpentKey, sessionKey, sessionId } = useMemo(() => {
    // If the user is not authenticated, return null values for the keys
    if (!isAuthenticated()) {
      return {
        timeSpentKey: null,
        sessionKey: null,
        sessionId: null,
      };
    }
    // Otherwise, generate the keys and session ID using the user's ID
    return {
      timeSpentKey: `${STORAGE_KEYS.TIME_SPENT}-${user?.id}`, //unique key per user
      sessionKey: `${STORAGE_KEYS.SESSION_KEY}-${user?.id}`, //unique key per user
      sessionId: uuidv4(), //unique key per session
    };
  }, [user?.id]);

  /**
   * Check if the session can be refreshed (refresh means update the timestamp while keeping the same session ID),
   * the session can be refreshed if last session has expired, doesn't exist or the current session ID matches the last saved session ID
   * @param sessionKey - The key for the session in local storage
   * @param sessionId - The ID of the current session
   * @returns Whether the session can be refreshed
   */
  function canRefreshSession({
    sessionKey,
    sessionId,
  }: Record<'sessionKey' | 'sessionId', string>) {
    // Get the last saved session string from local storage
    const lastSavedSessionString = localStorage.getItem(sessionKey);
    // If there is no last saved session string, the session can be refreshed
    if (!lastSavedSessionString) {
      return true;
    }
    // Otherwise, parse the session ID and timestamp from the string
    const lastSavedSessionId = lastSavedSessionString?.split('_')?.[0];
    const lastSavedSessionTimeStamp = lastSavedSessionString?.split('_')?.[1];
    // Check if the last saved session has expired
    const lastSavedSessionExpired = dayjs(lastSavedSessionTimeStamp).isBefore(
      dayjs().subtract(1.5, 'minute')
    );
    // If the current session ID matches the last saved session ID or the last saved session has expired, the session can be refreshed
    return sessionId === lastSavedSessionId || lastSavedSessionExpired;
  }

  /**
   * Refresh the session in local storage
   * Keeps the current session ID and update the timestamp
   * @param sessionKey - The key for the session in local storage
   * @param sessionId - The ID of the current session
   * @returns The new session string
   */
  function refreshSession({
    sessionKey,
    sessionId,
  }: Record<'sessionKey' | 'sessionId', string>) {
    // Generate a new session string with the current session ID and timestamp
    const newSessionString = `${sessionId}_${dayjs().format()}`;
    // Save the new session string to local storage
    localStorage.setItem(sessionKey, newSessionString);
    // Return the new session string
    return newSessionString;
  }

  /**
   * Save the time spent to local storage
   * @param timeSpentKey - The key for the time spent in local storage
   * @param sessionKey - The key for the session in local storage
   * @param sessionId - The ID of the current session
   */
  function saveTimeSpent({
    timeSpentKey,
    sessionKey,
    sessionId,
  }: Record<'timeSpentKey' | 'sessionKey' | 'sessionId', string>) {
    // If the window is hidden, the session cannot be refreshed
    if (!isWindowFocused()) {
      restartSaveTimeSpentInterval();
      return;
    }
    // If the session cannot be refreshed, do not save the time spent
    if (!canRefreshSession({ sessionKey, sessionId })) {
      return;
    }
    // Otherwise, refresh the session and get the time spent from local storage
    refreshSession({ sessionKey, sessionId });
    const timeSpentSting = localStorage.getItem(timeSpentKey);
    // Parse the time spent as a map of start times to end times
    const timeSpent: TimeSpentMap = JSON.parse(timeSpentSting || '{}') || {};
    // Get the new start and end times
    const newStartTime = dayjs(startTime).startOf('minute').format();
    const newEndTime = dayjs().startOf('minute').format();
    // If the start and end times are the same, do not save the time spent
    if (newStartTime === newEndTime) {
      return;
    }
    // Otherwise, add the new time spent to the map and save it to local storage
    timeSpent[newStartTime] = newEndTime;
    const newTimeSpentString = JSON.stringify(timeSpent);
    localStorage.setItem(timeSpentKey, newTimeSpentString);
  }

  /**
   * Restart the save time spent interval
   */
  function restartSaveTimeSpentInterval() {
    // Set the start time to the current time and clear the save and send intervals
    setStartTime(dayjs());
    clearInterval(saveTimeSpentIntervalRef.current);
    saveTimeSpentIntervalRef.current = undefined;
    clearInterval(sendSavedTimeSpentIntervalRef.current);
    sendSavedTimeSpentIntervalRef.current = undefined;
  }

  /**
   * Send the saved time spent to the API
   * @param timeSpentKey - The key for the time spent in local storage
   * @param sessionKey - The key for the session in local storage
   * @param sessionId - The ID of the current session
   */
  async function sendSavedTimeSpent({
    timeSpentKey,
    sessionKey,
    sessionId,
  }: Record<'timeSpentKey' | 'sessionKey' | 'sessionId', string>) {
    // If the session cannot be refreshed, do not send the time spent
    if (!canRefreshSession({ sessionKey, sessionId })) {
      return;
    }
    // Otherwise, get the time spent from local storage and sort it by start time
    const timeSpentSting = localStorage.getItem(timeSpentKey);
    const timeSpent: TimeSpentMap = JSON.parse(timeSpentSting || '{}') || {};
    const timeSpentArray = Object.entries(timeSpent).sort((t1, t2) =>
      dayjs(t1[0]).isBefore(dayjs(t2[0])) ? -1 : 1
    );

    // If there is no time spent, do not send anything
    if (!timeSpentArray?.length) {
      return;
    }

    // Aggregate the time spent into intervals
    const aggregatedTimeSpent = timeSpentArray.reduce(
      (acc, curr, index) => {
        // Skip the first item because it is already in the accumulator
        if (index === 0) {
          return acc;
        }
        const newAcc = [...acc];
        const lastIndex = acc?.length - 1;
        // If the current time spent interval starts where the previous one ends, merge them
        if (acc?.[lastIndex]?.endTime === curr?.[0]) {
          newAcc[lastIndex] = {
            ...acc[lastIndex],
            endTime: curr[1],
          };
        } else {
          // Otherwise, add a new time spent interval
          newAcc.push({
            startTime: curr[0],
            endTime: curr[1],
          });
        }
        return newAcc;
      },
      [
        {
          startTime: timeSpentArray?.[0]?.[0],
          endTime: timeSpentArray?.[0]?.[1],
        },
      ]
    );

    // Send the aggregated time spent to the API
    await batchLogTimeSpentApi?.(aggregatedTimeSpent);

    // If it doesn't error filter out the time spent that wasn't sent from local storage
    // And save the filtered time spent back to local storage
    const latestTimeSpent: TimeSpentMap = JSON.parse(
      localStorage.getItem(timeSpentKey) || '{}'
    ); // Latest time spent from local storage, may have changed since the last time we read from it
    const cleanedTimeSpentToSave: TimeSpentMap = {}; // Time spent to save back to local storage
    Object.entries(latestTimeSpent).forEach(([startTime, endTime]) => {
      //If the time wasn't in the sent time spent, add it to the cleaned time spent
      if (!timeSpent?.[startTime]) {
        cleanedTimeSpentToSave[startTime] = endTime;
      }
      //If the time was in the sent time spent, but the end time is different, dissect add it to the cleaned time spent
      if (timeSpent?.[startTime] !== endTime) {
        const timeSpentEndTime = dayjs(timeSpent?.[startTime]);
        //If the time spent end time is before the end time from local storage, add it to the cleaned time spent
        //This might happen for example if the time {"2021-09-28T15:00:00.000Z":"2021-09-28T15:01:00.000Z"} was sent to the API
        //and the time spent in local storage is now {"2021-09-28T15:00:00.000Z":"2021-09-28T15:02:00.000Z"}
        //In this case we want to save {"2021-09-28T15:01:00.000Z":"2021-09-28T15:02:00.000Z"} back to local storage
        //since it wasn't sent to the API
        if (timeSpentEndTime?.isBefore(dayjs(endTime))) {
          cleanedTimeSpentToSave[timeSpentEndTime.format()] = endTime;
        }
      }
    });

    localStorage.setItem(timeSpentKey, JSON.stringify(cleanedTimeSpentToSave));
    restartSaveTimeSpentInterval();
  }

  useEffect(() => {
    if (!isWindowFocused()) {
      restartSaveTimeSpentInterval();
      return;
    }
  }, [isWindowFocused()]);

  // Set up the save time spent interval
  useEffect(() => {
    if (!mounted) {
      return;
    }
    // If the time spent key, session key, or session ID are null, do not set up the interval
    if (!timeSpentKey || !sessionId || !sessionKey) {
      return;
    }
    // If the save time spent interval is already set up, do not set it up again
    if (saveTimeSpentIntervalRef.current) {
      return;
    }

    // Set up the save time spent interval to save the time spent every minute
    saveTimeSpentIntervalRef.current = setInterval(
      () =>
        saveTimeSpent({
          timeSpentKey,
          sessionId,
          sessionKey,
        }),
      ONE_MINUTE
    );
    // Clean up the save time spent interval on unmount
    return () => {
      clearInterval(saveTimeSpentIntervalRef.current);
    };
  }, [timeSpentKey, startTime]);

  // Set up the send saved time spent interval
  useEffect(() => {
    // If the time spent key, session key, or session ID are null, do not set up the interval
    if (!timeSpentKey || !sessionId || !sessionKey) {
      return;
    }
    // If the send saved time spent interval is already set up, do not set it up again
    if (sendSavedTimeSpentIntervalRef.current) {
      return;
    }
    // Send the saved time spent immediately and set up the interval to send it every 10 minutes and 10 seconds
    sendSavedTimeSpent({
      timeSpentKey,
      sessionId,
      sessionKey,
    });
    sendSavedTimeSpentIntervalRef.current = setInterval(
      () =>
        sendSavedTimeSpent({
          timeSpentKey,
          sessionId,
          sessionKey,
        }),
      TEN_MINUTES_AND_10_SECONDS
    );
    // Clean up the send saved time spent interval on unmount
    return () => {
      clearInterval(sendSavedTimeSpentIntervalRef.current);
    };
  }, [timeSpentKey, startTime]);

  // Render the time spent provider
  if (!mounted) {
    return null;
  }

  return (
    <AscTimeSpentContext.Provider value={{}}>
      {children}
    </AscTimeSpentContext.Provider>
  );
}
